import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { DiscountType } from '../enums/DiscountType';
import { CLIPBOARD_SHORTCUT_KEYS, NAVIGATION_KEYS } from '../constants/keyboard.constants';

export function handleDiscountKeyDown(event: KeyboardEvent): void {
  if (isDiscountClipboardShortcut(event) || NAVIGATION_KEYS.includes(event.key)) {
    return;
  }
  if (!/^\d$/.test(event.key)) {
    event.preventDefault();
    return;
  }
  const input = event.target as HTMLInputElement;
  const caret = input.selectionStart ?? input.value.length;
  if (event.key === '0' && !input.value.slice(0, caret)) {
    event.preventDefault();
  }
}

export function sanitizeDiscountPaste(event: ClipboardEvent): number | '' {
  event.preventDefault();
  const input = event.target as HTMLInputElement;
  const pasted = event.clipboardData?.getData('text') ?? '';
  const start = input.selectionStart ?? input.value.length;
  const end = input.selectionEnd ?? input.value.length;
  const merged = `${input.value.slice(0, start)}${pasted}${input.value.slice(end)}`;
  const digitsOnly = merged.replace(/\D/g, '');
  return digitsOnly === '' ? '' : Number(digitsOnly);
}

function isDiscountClipboardShortcut(event: KeyboardEvent): boolean {
  return (event.ctrlKey || event.metaKey) && CLIPBOARD_SHORTCUT_KEYS.includes(event.key.toLowerCase());
}

export function handleDiscountReasonKeyDown(event: KeyboardEvent): void {
  if (event.key !== ' ') {
    return;
  }
  const input = event.target as HTMLInputElement;
  const caret = input.selectionStart ?? input.value.length;
  if (!input.value.slice(0, caret).trim()) {
    event.preventDefault();
  }
}

export function sanitizeDiscountReasonPaste(event: ClipboardEvent): string {
  event.preventDefault();
  const input = event.target as HTMLInputElement;
  const pasted = event.clipboardData?.getData('text') ?? '';
  const start = input.selectionStart ?? input.value.length;
  const end = input.selectionEnd ?? input.value.length;
  const merged = `${input.value.slice(0, start)}${pasted}${input.value.slice(end)}`;
  return merged.replace(/^\s+/, '');
}

export function integerValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (value === null || value === '' || value === undefined) return null;
    return Number.isInteger(Number(value)) ? null : { notInteger: true };
  };
}

function withoutKeys(errors: ValidationErrors | null, keys: string[]): ValidationErrors {
  if (!errors) return {};
  const result = { ...errors };
  keys.forEach(k => delete result[k]);
  return result;
}

function nullIfEmpty(errors: ValidationErrors): ValidationErrors | null {
  return Object.keys(errors).length > 0 ? errors : null;
}

export function syncDiscountControlErrors(
  discountTypeCtrl: AbstractControl,
  discountValueCtrl: AbstractControl,
  basePrice: number
): void {
  const discountValue: number = Number(discountValueCtrl.value) || 0;
  const discountType: string = discountTypeCtrl.value;

  const typeErrors = withoutKeys(discountTypeCtrl.errors, ['discountTypeRequired']);
  const valueErrors = withoutKeys(discountValueCtrl.errors, ['maxPercent', 'exceedsPrice']);

  if (discountValue > 0 && !discountType) {
    discountTypeCtrl.setErrors({ ...typeErrors, discountTypeRequired: true });
    discountTypeCtrl.markAsTouched();
  } else {
    discountTypeCtrl.setErrors(nullIfEmpty(typeErrors));
  }

  if (discountType === DiscountType.PERCENT && discountValue >= 100) {
    discountValueCtrl.setErrors({ ...valueErrors, maxPercent: true });
  } else if (discountType === DiscountType.AMD && basePrice > 0 && discountValue > basePrice) {
    discountValueCtrl.setErrors({ ...valueErrors, exceedsPrice: true });
  } else {
    discountValueCtrl.setErrors(nullIfEmpty(valueErrors));
  }
}
